import { Link } from 'react-router-dom';
import AppStoreBadge from '@/components/common/AppStoreBadge';
import PlayStoreBadge from '@/components/common/PlayStoreBadge';

const faqs = [
  {
    q: 'What is Polymyth?',
    a: 'Polymyth is a role-playing adventure platform compatible with fifth edition. It runs adventures using the 2024 SRD rules, handling combat, roleplay, and storytelling, so you can play without needing a human GM.',
  },
  {
    q: 'Do I need to know the rules?',
    a: 'No! The AI handles all the complex mechanics. You just describe what your character does, and the game figures out the rest. Experienced players will recognize familiar fifth edition mechanics.',
  },
  {
    q: 'Can I play solo?',
    a: 'Absolutely. You can play with just your character (and NPC companions) anytime. No scheduling, no waiting.',
  },
  {
    q: 'Can I play with friends?',
    a: 'Yes! Ask Tavian at the Hilltop Camp to invite your friend by hero name; they accept with one click and you adventure together, each playing their own hero. NPC companions can fill empty slots if you want a fuller party. See the full step-by-step guide on the Playing with Friends page.',
  },
  {
    q: 'What happens if a friend disconnects mid-adventure?',
    a: 'The party plays on. After a grace period their hero either fights on under AI control or steps out of the scene, per their settings, and they can rejoin anytime to take back control. If the leader drops, leadership passes automatically until they return.',
  },
  {
    q: 'How do I get access?',
    a: 'Just sign up. No invite code, no waiting list. Create an account on the home page and start playing right away, free to start.',
  },
  {
    q: 'What adventures are available?',
    a: "Two original adventures built for Polymyth are live now: Goldvale's Last Hope and The Bleeding Gate, both for levels 1-3. We add more regularly.",
  },
  {
    q: 'Is it free?',
    a: 'Free to start: every new account gets starter credits to play right away. When they run out, you can top up to keep going.',
  },
  {
    q: 'What rules does it use?',
    a: 'Polymyth is compatible with fifth edition and uses the SRD 5.2.1 (2024) as its rule base, with some homebrew adaptations for smoother digital play.',
  },
];

const mobileFaqs = [
  {
    q: 'Is there a mobile app?',
    a: 'Yes. Polymyth is live on the App Store for iPhone and iPad and on Google Play for Android. You can also play in your desktop browser.',
  },
  {
    q: 'Which devices can I play on?',
    a: 'iPhone and iPad via the App Store, Android via Google Play, plus any desktop browser. The mobile app is built natively for the small screen.',
  },
  {
    q: 'Does my progress sync across devices?',
    a: 'Yes. Everything is tied to your account — start a session on your iPhone and pick it up on iPad or in your browser, right where you left off.',
  },
  {
    q: 'Do I need an invite code on mobile?',
    a: 'No. Download the app, create an account, and start playing. Same open signup on mobile and web alike.',
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <div className="border-b border-border/50 pb-6">
      <h3 className="text-lg font-semibold text-foreground mb-2">{q}</h3>
      <p className="text-lg font-medium text-foreground/70 leading-relaxed">{a}</p>
    </div>
  );
}

export default function FaqPage() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-20">
      <h1 className="text-4xl font-bold mb-8 text-foreground">Frequently Asked Questions</h1>

      <div className="space-y-6">
        {faqs.map((faq) => (
          <FaqItem key={faq.q} q={faq.q} a={faq.a} />
        ))}
      </div>

      <p className="mt-8 text-lg font-medium text-foreground/70 leading-relaxed">
        Playing with friends? Read the step-by-step{' '}
        <Link to="/multiplayer" className="text-primary underline underline-offset-4">
          Playing with Friends
        </Link>{' '}
        guide.
      </p>

      {/* Mobile */}
      <div className="mt-14 flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-2xl font-bold text-foreground">Mobile</h2>
          <div className="flex flex-wrap items-center gap-3">
            <AppStoreBadge />
            <PlayStoreBadge />
          </div>
        </div>
        <div className="space-y-6">
          {mobileFaqs.map((faq) => (
            <FaqItem key={faq.q} q={faq.q} a={faq.a} />
          ))}
        </div>
      </div>
    </div>
  );
}
